
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { PlayerStats } from '@/types/match';
import { isValidUUID } from '@/utils/uuidUtils';

// Define a simplified interface for this hook's return type
export interface PlayerStatsHookReturn {
  stats: PlayerStats;
  loading: boolean;
  error?: string | null;
  availableYears?: number[];
  rank?: number;
}

const DEFAULT_STATS: PlayerStats = {
  totalMatches: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  winRate: 0,
  skillRating: 5.0,
  followersCount: 0,
  followingCount: 0,
  points: { total: 0, group: 0, private: 0 },
  topPartners: [],
  topOpponents: [],
  bestTeams: [],
  worstTeams: [],
  mostFrequentOpponent: null,
  mostFrequentPartner: null,
  biggestRival: null,
  worstPartner: null,
  mostDefeatedOpponent: null,
  worstMonth: null,
  bestMonth: null,
};

export const usePlayerStats = (userId?: string, year?: number): PlayerStatsHookReturn => {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;

  const { data, isLoading, error } = useQuery({
    queryKey: ['player-stats', targetUserId, year ?? null],
    queryFn: async () => {
      if (!targetUserId || !isValidUUID(targetUserId)) {
        throw new Error(!targetUserId ? 'No user ID provided' : 'Invalid user ID format');
      }

      // Phase 1: Fast path — aggregated core stats from RPC
      const { data: core, error: coreError } = await supabase
        .rpc('get_player_stats_optimized', {
          p_user_id: targetUserId,
          p_year: year ?? null,
        })
        .maybeSingle();

      if (coreError) throw coreError;

      const bestMonth = core?.best_month_name
        ? {
            month: 0,
            monthName: core.best_month_name as string,
            wins: Number(core.best_month_wins ?? 0),
            losses: Number(core.best_month_losses ?? 0),
            total: Number(core.best_month_wins ?? 0) + Number(core.best_month_losses ?? 0),
            winRate: Number(core.best_month_win_rate ?? 0),
          }
        : null;

      const worstMonth = core?.worst_month_name
        ? {
            month: 0,
            monthName: core.worst_month_name as string,
            wins: Number(core.worst_month_wins ?? 0),
            losses: Number(core.worst_month_losses ?? 0),
            total: Number(core.worst_month_wins ?? 0) + Number(core.worst_month_losses ?? 0),
            winRate: Number(core.worst_month_win_rate ?? 0),
          }
        : null;

      const globalRank = core?.global_rank != null ? Number(core.global_rank) : undefined;

      const coreStats: PlayerStats = {
        ...DEFAULT_STATS,
        totalMatches: Number(core?.total_matches ?? 0),
        wins: Number(core?.wins ?? 0),
        losses: Number(core?.losses ?? 0),
        draws: Number(core?.draws ?? 0),
        winRate: Number(core?.win_rate ?? 0),
        skillRating: Number(core?.skill_rating ?? 5.0),
        followersCount: Number(core?.followers_count ?? 0),
        followingCount: Number(core?.following_count ?? 0),
        currentStreak: core?.current_streak ?? undefined,
        currentStreakType: (core?.current_streak_type as 'win' | 'loss' | 'draw' | undefined) ?? undefined,
        points: {
          total: Number(core?.total_points ?? 0),
          group: Number(core?.group_points ?? 0),
          private: Number(core?.private_points ?? 0),
        },
        bestMonth,
        worstMonth,
      };

      // Phase 2: Progressive enhancement — partnership/opponent stats
      const { data: matchPlayers, error: matchError } = await supabase
        .from('match_players')
        .select(`
          match_id,
          team,
          matches!inner (
            winner_team,
            validation_status,
            date
          )
        `)
        .eq('user_id', targetUserId);

      if (matchError) {
        // Return core stats without partnership data
        return { stats: coreStats, rank: globalRank };
      }

      const validatedMatches = (matchPlayers || []).filter(
        (mp: any) => mp.matches?.validation_status === 'validated'
      );

      if (validatedMatches.length === 0) {
        return { stats: coreStats, rank: globalRank };
      }

      const { data: allMatchPlayers, error: allMatchPlayersError } = await supabase
        .from('match_players')
        .select(`
          match_id,
          team,
          user_id,
          matches!inner (
            validation_status,
            winner_team
          )
        `)
        .in('match_id', validatedMatches.map((m: any) => m.match_id));

      if (allMatchPlayersError) {
        return { stats: coreStats, rank: globalRank };
      }

      const matchTeams: Record<string, Record<number, string[]>> = {};
      (allMatchPlayers || []).forEach((mp: any) => {
        if (!matchTeams[mp.match_id]) matchTeams[mp.match_id] = { 1: [], 2: [] };
        if (mp.user_id) matchTeams[mp.match_id][mp.team].push(mp.user_id);
      });

      const partnerStats: Record<string, { matches: number; wins: number; losses: number }> = {};
      const opponentStats: Record<string, { matches: number; wins: number; losses: number }> = {};

      validatedMatches.forEach((userMatch: any) => {
        const matchId = userMatch.match_id;
        const userTeam = userMatch.team;
        const opponentTeam = userTeam === 1 ? 2 : 1;
        const won = userMatch.matches.winner_team === userTeam;

        const partners = (matchTeams[matchId]?.[userTeam] || []).filter((id: string) => id !== targetUserId);
        partners.forEach((partnerId: string) => {
          if (!partnerStats[partnerId]) partnerStats[partnerId] = { matches: 0, wins: 0, losses: 0 };
          partnerStats[partnerId].matches++;
          if (won) partnerStats[partnerId].wins++; else partnerStats[partnerId].losses++;
        });

        const opponents = matchTeams[matchId]?.[opponentTeam] || [];
        opponents.forEach((opponentId: string) => {
          if (!opponentStats[opponentId]) opponentStats[opponentId] = { matches: 0, wins: 0, losses: 0 };
          opponentStats[opponentId].matches++;
          if (won) opponentStats[opponentId].losses++; else opponentStats[opponentId].wins++;
        });
      });

      const allPlayerIds = [...new Set([...Object.keys(partnerStats), ...Object.keys(opponentStats)])];

      if (allPlayerIds.length === 0) {
        return { stats: coreStats, rank: globalRank };
      }

      const { data: playerProfiles, error: playerProfilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', allPlayerIds);

      if (playerProfilesError) {
        return { stats: coreStats, rank: globalRank };
      }

      const profileMap = new Map((playerProfiles || []).map((p: any) => [p.id, p.full_name || 'Unknown']));

      const topPartners = Object.entries(partnerStats)
        .map(([id, pst]) => ({
          id,
          name: profileMap.get(id) || 'Unknown',
          username: '',
          avatarUrl: '',
          matches: pst.matches,
          wins: pst.wins,
          losses: pst.losses,
          winRate: Math.round((pst.wins / pst.matches) * 100),
        }))
        .sort((a, b) => b.matches - a.matches)
        .slice(0, 5);

      const topOpponents = Object.entries(opponentStats)
        .map(([id, ost]) => ({
          id,
          name: profileMap.get(id) || 'Unknown',
          username: '',
          avatarUrl: '',
          matches: ost.matches,
          winsAgainst: ost.losses,
          lossesAgainst: ost.wins,
          winRateAgainst: Math.round((ost.losses / ost.matches) * 100),
        }))
        .sort((a, b) => b.matches - a.matches)
        .slice(0, 5);

      const mostFrequentPartner = topPartners.length > 0 ? topPartners[0] : null;
      const mostFrequentOpponent = topOpponents.length > 0 ? topOpponents[0] : null;
      const biggestRival = topOpponents.length > 0
        ? topOpponents.reduce((rival, opp) => opp.winRateAgainst < rival.winRateAgainst ? opp : rival)
        : null;
      const mostDefeatedOpponent = topOpponents.length > 0
        ? topOpponents.reduce((defeated, opp) => opp.winRateAgainst > defeated.winRateAgainst ? opp : defeated)
        : null;
      const worstPartner = topPartners.length > 0
        ? topPartners.reduce((worst, partner) => partner.winRate < worst.winRate ? partner : worst)
        : null;

      return {
        stats: {
          ...coreStats,
          topPartners,
          topOpponents,
          mostFrequentOpponent,
          mostFrequentPartner,
          biggestRival,
          worstPartner,
          mostDefeatedOpponent,
          bestTeams: [],
          worstTeams: [],
        },
        rank: globalRank,
      };
    },
    enabled: !!targetUserId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    stats: data?.stats ?? DEFAULT_STATS,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    availableYears: [],
    rank: data?.rank,
  };
};
